/**
 * LoopPanel — the venture's single daily execution plan.
 *
 * Replaces the legacy DailyBriefsPanel (4 disjoint briefs/day).
 *
 * Structure (top → bottom):
 *   1. <LoopState />          State of the venture (Build/Package/Sell stages)
 *   2. <BlockingQuestions />  Up to 3 questions Aria/Lyra/Hermes need answered
 *                              before they can ship — each opens a chat drawer.
 *   3. <TodaysTriangle />     5-7 atoms × 3 agents · drag/ship to Office.
 *   4. <ReadyForReview />     Artifacts waiting for founder's Validate/Modify/
 *                              Cancel decision (the SACRED human-in-the-loop).
 *
 * Endpoints used:
 *   GET  /api/projects/{id}/loop/today                 → load
 *   POST /api/projects/{id}/loop/compose               → fresh compose
 *   POST /api/loops/{loopId}/questions/{qid}/answer    → unlock atoms
 *   POST /api/loops/{loopId}/atoms/{taskId}/ship       → push to Office
 *
 * Design coherent with Workshop (border-radius 14-16px, glass surfaces,
 * per-agent gradients, every interactive element data-testid'd).
 */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Compass, PenTool, Wrench, Send, Loader2, ChevronRight,
  AlertOctagon, Target, CheckCircle2,
  Rocket, HelpCircle,
} from 'lucide-react';
import ValidationModal from './ValidationModal';
import DriftBadge from '../aria/DriftBadge';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AGENT_META = {
  aria:   { name: 'Aria',   role: 'The Architect',   pillar: 'Build',   accent: '#7C3AED', soft: '#A78BFA', gradient: 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)', Icon: Wrench },
  lyra:   { name: 'Lyra',   role: 'The Storyteller', pillar: 'Package', accent: '#FF6B1A', soft: '#FFD166', gradient: 'linear-gradient(135deg, #FFD166 0%, #FF6B1A 100%)', Icon: PenTool },
  hermes: { name: 'Hermes', role: 'The Pathfinder',  pillar: 'Sell',    accent: '#FFB347', soft: '#FFE89D', gradient: 'linear-gradient(135deg, #FFE89D 0%, #FFB347 100%)', Icon: Compass },
};

const normalizeOwner = (o) => {
  const x = String(o || '').toLowerCase();
  if (['markus', 'marcus', 'revenue', 'sales', 'growth'].includes(x)) return 'hermes';
  if (['builder', 'aria'].includes(x)) return 'aria';
  if (['content', 'lyra'].includes(x)) return 'lyra';
  if (['hermes'].includes(x)) return 'hermes';
  return 'aria';
};

export default function LoopPanel({ projectId, onSelectQuestion, onSelectTask, onLoopLoaded, onOpenInWorkshop }) {
  const [loop, setLoop] = useState(null);
  const [composing, setComposing] = useState(false);
  const [reviewTask, setReviewTask] = useState(null);
  const [err, setErr] = useState(null);

  // Initial load + poll while a compose is running in background.
  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    let pollTimer = null;

    const load = async () => {
      try {
        const r = await fetch(`${API}/api/projects/${projectId}/loop/today`, { credentials: 'include' });
        if (!r.ok) {
          if (!cancelled) setErr(`HTTP ${r.status}`);
          return;
        }
        const data = await r.json();
        if (cancelled) return;
        setLoop(data);
        onLoopLoaded?.(data);
        // Surface the backend compose error inline (no full-screen takeover).
        setErr(data.status === 'failed' ? (data.error || 'Composition failed') : null);
        // Keep polling while a compose is in flight.
        if (data.status === 'composing') {
          pollTimer = setTimeout(load, 4000);
        }
      } catch (e) {
        if (!cancelled) setErr(String(e?.message || e));
      }
    };

    load();
    return () => { cancelled = true; if (pollTimer) clearTimeout(pollTimer); };
  }, [projectId]);

  // Live-poll the loop every 5s when an atom is in motion (any chat-derived
  // atom NOT in backlog and NOT in done) — this is what makes the founder
  // see briefing → thinking → executing → review pass live after asking
  // Hermes/Aria/Lyra to execute the atoms they just proposed.
  useEffect(() => {
    if (!projectId || !loop) return undefined;
    const inMotion = (loop.atoms || []).some(a => {
      const s = a.current_state;
      return s && s !== 'backlog' && s !== 'done';
    });
    if (!inMotion) return undefined;
    const timer = setInterval(refresh, 5000);
    return () => clearInterval(timer);
  // refresh is stable enough — only key on the project + the in-motion shape
  // so the interval doesn't reset every tick.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, loop?.atoms?.map(a => `${a.task_id}:${a.current_state}`).join('|')]);

  const refresh = async () => {
    const r = await fetch(`${API}/api/projects/${projectId}/loop/today`, { credentials: 'include' });
    if (r.ok) {
      const data = await r.json();
      setLoop(data);
      onLoopLoaded?.(data);
      setErr(data.status === 'failed' ? (data.error || 'Composition failed') : null);
    }
  };

  const composeFresh = async () => {
    setComposing(true);
    try {
      const r = await fetch(`${API}/api/projects/${projectId}/loop/compose`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) {
        const data = await r.json();
        setLoop(data);          // immediately shows the placeholder + composing badge
        setErr(null);
        // Poll until status flips out of 'composing'.
        let cancelled = false;
        const poll = async () => {
          if (cancelled) return;
          const r2 = await fetch(`${API}/api/projects/${projectId}/loop/today`, { credentials: 'include' });
          if (r2.ok) {
            const d2 = await r2.json();
            setLoop(d2);
            if (d2.status === 'composing') setTimeout(poll, 4000);
            else { setComposing(false); setErr(d2.status === 'failed' ? (d2.error || 'Composition failed') : null); }
          }
        };
        setTimeout(poll, 4000);
      } else {
        setErr(`Compose HTTP ${r.status}`);
        setComposing(false);
      }
    } catch (e) {
      setErr(String(e?.message || e));
      setComposing(false);
    }
  };

  const shipAtom = async (taskId) => {
    if (!loop) return;
    const r = await fetch(`${API}/api/loops/${loop.loop_id}/atoms/${taskId}/ship`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (r.ok) {
      // Optimistic local update
      setLoop(prev => prev ? {
        ...prev,
        atoms: (prev.atoms || []).map(a =>
          a.task_id === taskId ? { ...a, current_state: 'briefing' } : a
        ),
      } : prev);
    }
  };

  // Trigger the agent's autonomous recipe (e.g. Hermes lead-finding via
  // LeadMagic + Context.dev). Backend lands the artifact in `review` so
  // the Validation Modal can close the loop.
  const runAtom = async (taskId) => {
    setLoop(prev => prev ? {
      ...prev,
      atoms: (prev.atoms || []).map(a =>
        a.task_id === taskId ? { ...a, current_state: 'executing' } : a
      ),
    } : prev);
    try {
      const r = await fetch(`${API}/api/tasks/${taskId}/run`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => ({}));
        setErr(detail?.detail || `Run failed (HTTP ${r.status})`);
      }
    } finally {
      await refresh();
    }
  };

  // ALWAYS render the structural skeleton — never the page-takeover loader.
  // The frame stays; the inner sections adapt to seeding / composing / failed.
  const safeLoop = loop || {
    loop_id: null, project_id: projectId, status: 'seeding',
    state: {}, questions: [], atoms: [], atom_task_ids: [],
  };
  const isComposing = safeLoop.status === 'composing' || safeLoop.status === 'seeding' || composing;
  const isFailed = safeLoop.status === 'failed' || (!!err && !loop);
  const openAtoms = (safeLoop.atoms || []).filter(a => a.current_state !== 'done');
  // Tasks born from the chat (Hermes-spawned per-recipient deliverables OR
  // atoms added by Aria via `propose_loop_atom`) live ABOVE the original
  // Build · Package · Sell triangle. The triangle stays focused on the
  // strategic atoms Aria seeded — no duplicates with the Ship section.
  const isChatDerived = (a) => !!a.parent_task_id || a.origin === 'aria_proposal';
  const seededAtoms = openAtoms.filter(a => !isChatDerived(a));
  // Show ALL chat-derived atoms regardless of state so the founder watches
  // them physically move backlog → briefing → thinking → executing → review
  // in real time after asking "exécute" in the chat. Sorted with the most
  // advanced ones (review) first so the eye lands on action items.
  const STATE_RANK = { review: 0, executing: 1, consulting: 2, thinking: 3, briefing: 4, backlog: 5 };
  const additionalAtoms = openAtoms
    .filter(isChatDerived)
    .slice()
    .sort((a, b) =>
      (STATE_RANK[a.current_state] ?? 9) - (STATE_RANK[b.current_state] ?? 9)
    );

  return (
    <div data-testid="loop-panel" style={{
      padding: '24px 28px', minHeight: '100%',
      background: 'linear-gradient(180deg, #FBFAF7 0%, #F4F3F0 100%)',
      color: '#0F0F1A',
    }}>
      <LoopHeader loop={safeLoop} projectId={projectId} />
      {/* Subtle inline error banner — never replaces the frame. */}
      {isFailed && (
        <div data-testid="loop-error-banner" style={{
          padding: '10px 14px', marginBottom: 14,
          background: '#FEF2F2', border: '1px solid #FECACA',
          borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 12, color: '#7F1D1D',
        }}>
          <AlertOctagon size={14} />
          <span style={{ flex: 1 }}>
            Aria couldn&apos;t finish composing the execution plan — {err || safeLoop.error || 'unknown'}.
          </span>
          <button onClick={composeFresh} disabled={isComposing} style={{
            fontSize: 10, fontWeight: 800, color: '#FFFFFF',
            background: '#B91C1C', padding: '4px 10px', borderRadius: 999,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            border: 'none', cursor: isComposing ? 'wait' : 'pointer',
          }}>Retry</button>
        </div>
      )}
      <LoopState state={safeLoop.state || {}} composing={isComposing} />
      <BlockingQuestions
        questions={safeLoop.questions || []}
        composing={isComposing}
        onOpen={onSelectQuestion}
      />
      {/* Strategic atoms FIRST (the plan Aria seeded) — then the tactical
          additional tasks born from chat (Hermes recipe children, atoms
          added via propose_loop_atom, etc.). */}
      <TodaysTriangle
        atoms={seededAtoms}
        questions={safeLoop.questions || []}
        composing={isComposing}
        onShip={shipAtom}
        onRun={runAtom}
        onSelect={onSelectTask}
      />
      <ReadyForReview
        items={additionalAtoms}
        onReview={setReviewTask}
        onOpenInWorkshop={onOpenInWorkshop}
      />

      {/* Validation modal — the SACRED moment */}
      {reviewTask && (
        <ValidationModal
          task={reviewTask}
          onClose={() => setReviewTask(null)}
          onDone={async () => { setReviewTask(null); await refresh(); }}
        />
      )}
    </div>
  );
}

/* ====================== HEADER ====================== */
function LoopHeader({ loop, projectId }) {
  const seeded = loop?.status === 'composed';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      paddingBottom: 18, borderBottom: '1px solid rgba(15,15,30,0.06)',
      marginBottom: 18,
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 24, height: 1, background: '#7C3AED' }} />
          <span style={{
            fontSize: 10, fontWeight: 800, letterSpacing: '0.3em',
            textTransform: 'uppercase', color: '#7C3AED',
          }}>
            Execution Plan
          </span>
          <DriftBadge projectId={projectId} inline />
        </div>
        <h1 style={{
          margin: '8px 0 0 0', fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em',
          color: '#0F0F1A',
        }}>
          Build · Package · Sell
        </h1>
        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
          {seeded
            ? 'Composed by Aria · live backlog · atoms vanish as agents ship them'
            : 'Aria will seed this plan right after the Revenue Plan is ready'}
        </div>
      </div>
    </div>
  );
}

function StageBar({ label, done, total, agentKey }) {
  const pct = total > 0 ? Math.min(100, (done / total) * 100) : 0;
  const meta = AGENT_META[agentKey];
  return (
    <div data-testid={`loop-state-${label.toLowerCase()}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{
          fontSize: 10, fontWeight: 800, letterSpacing: '0.2em',
          textTransform: 'uppercase', color: meta.accent,
        }}>{label}</span>
        <span style={{ fontSize: 11, color: '#6B7280' }}>
          {total > 0 ? `${done}/${total} shipped` : 'no atoms yet'}
        </span>
      </div>
      <div style={{
        width: '100%', height: 6, borderRadius: 999,
        background: `${meta.soft}30`, overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: meta.gradient,
          transition: 'width 0.6s ease-out',
        }} />
      </div>
    </div>
  );
}

function LoopState({ state, composing }) {
  return (
    <div data-testid="loop-state" style={{
      background: '#FFFFFF',
      border: '1px solid rgba(15,15,30,0.06)',
      borderRadius: 16, padding: 18,
      marginBottom: 18,
      opacity: composing ? 0.7 : 1,
      transition: 'opacity 0.3s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <Target size={14} color="#7C3AED" />
        <span style={{
          fontSize: 11, fontWeight: 800, letterSpacing: '0.22em',
          textTransform: 'uppercase', color: '#0F0F1A',
        }}>State of the Venture</span>
        <span style={{ flex: 1 }} />
        {composing && <ComposingChip />}
        {!composing && Number.isFinite(state.distance_to_first_dollar) && (
          <span style={{
            fontSize: 11, fontWeight: 700, color: '#7C3AED',
            background: '#7C3AED15', padding: '4px 12px', borderRadius: 999,
          }}>
            {state.distance_to_first_dollar} hops to first dollar
          </span>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginBottom: 12 }}>
        <StageBar label="Build"   done={state.build_done   || 0} total={state.build_total   || 0} agentKey="aria"   />
        <StageBar label="Package" done={state.package_done || 0} total={state.package_total || 0} agentKey="lyra"   />
        <StageBar label="Sell"    done={state.sell_done    || 0} total={state.sell_total    || 0} agentKey="hermes" />
      </div>
      {state.narrative && (
        <div style={{
          fontSize: 13, lineHeight: 1.5, color: '#4B4B5C',
          fontStyle: 'italic', borderLeft: '2px solid #7C3AED', paddingLeft: 12, marginTop: 8,
        }}>
          {state.narrative}
        </div>
      )}
    </div>
  );
}

// Tiny reusable chip used everywhere a section is partially blank during compose.
function ComposingChip() {
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, letterSpacing: '0.18em',
      color: '#7C3AED', background: '#EDE9FE',
      padding: '4px 10px', borderRadius: 999,
      display: 'flex', alignItems: 'center', gap: 6,
      textTransform: 'uppercase',
    }}>
      <Loader2 size={10} style={{ animation: 'spin 0.9s linear infinite' }} />
      Aria is seeding
    </span>
  );
}

/* ====================== BLOCKING QUESTIONS ====================== */
function BlockingQuestions({ questions, onOpen, composing }) {
  const open = questions.filter(q => q.status === 'open');
  // Always render the section while composing (even with 0 questions) so the
  // frame stays. Hide it only when we know there are 0 questions for sure.
  if (open.length === 0 && !composing) return null;
  return (
    <div data-testid="blocking-questions" style={{
      background: '#FFFFFF',
      border: composing ? '1px dashed rgba(15,15,30,0.12)' : '1px solid #EF444430',
      borderLeft: composing ? '3px solid rgba(15,15,30,0.2)' : '3px solid #EF4444',
      borderRadius: 16, padding: 18,
      marginBottom: 18,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <HelpCircle size={14} color={composing ? '#9CA3AF' : '#EF4444'} />
        <span style={{
          fontSize: 11, fontWeight: 800, letterSpacing: '0.22em',
          textTransform: 'uppercase', color: composing ? '#9CA3AF' : '#EF4444',
        }}>
          {composing ? 'Blockers — Aria is identifying what to unblock first' :
            `${open.length} question${open.length > 1 ? 's' : ''} need your answer before we ship`}
        </span>
      </div>
      {composing && open.length === 0 ? (
        <SkeletonRow count={2} />
      ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {open.map(q => {
          const agent = normalizeOwner(q.asked_by);
          const meta = AGENT_META[agent];
          return (
            <div
              key={q.question_id}
              data-testid={`question-${q.question_id}`}
              onClick={() => onOpen(q)}
              style={{
                padding: '12px 14px',
                background: '#FBFAF7',
                border: `1px solid ${meta.soft}40`,
                borderRadius: 12,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 12,
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateX(2px)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,15,30,0.06)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateX(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: meta.gradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <meta.Icon size={14} color="#FFFFFF" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 10, fontWeight: 800, letterSpacing: '0.16em',
                  color: meta.accent, textTransform: 'uppercase', marginBottom: 2,
                }}>
                  {meta.name} asks
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0F0F1A' }}>{q.question}</div>
                {q.why && (
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2, fontStyle: 'italic' }}>
                    {q.why}
                  </div>
                )}
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700, color: meta.accent,
                background: `${meta.soft}30`, padding: '4px 10px', borderRadius: 999,
                letterSpacing: '0.12em', textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                Open chat <ChevronRight size={11} />
              </span>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

// Generic shimmer row used by sections waiting for compose to land.
function SkeletonRow({ count = 3, height = 44 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{
          height, borderRadius: 12,
          background: 'linear-gradient(90deg, #F4F3F0 0%, #FBFAF7 50%, #F4F3F0 100%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.6s linear infinite',
          border: '1px solid rgba(15,15,30,0.04)',
        }} />
      ))}
      <style>{`
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>
    </div>
  );
}

/* ====================== READY FOR REVIEW ====================== */
function ReadyForReview({ items, onReview, onOpenInWorkshop }) {
  if (!items || items.length === 0) return null;
  const reviewCount = items.filter(t => t.current_state === 'review').length;
  const movingCount = items.length - reviewCount;
  // owner_agent_id → workshop folder key
  const ownerToFolder = (o) => {
    const k = normalizeOwner(o);
    return k === 'aria' ? 'builder' : k;   // Workshop uses 'builder' for Aria
  };
  // Lighter, agent-neutral state pill so the founder reads the journey at a glance.
  const STATE_PILL = {
    backlog:    { label: 'Backlog',    bg: '#F3F4F6', fg: '#6B7280', dot: '#9CA3AF' },
    briefing:   { label: 'Briefing',   bg: '#EDE9FE', fg: '#6D28D9', dot: '#7C3AED' },
    thinking:   { label: 'Thinking',   bg: '#DBEAFE', fg: '#1E40AF', dot: '#2563EB' },
    consulting: { label: 'Consulting', bg: '#DBEAFE', fg: '#1E40AF', dot: '#2563EB' },
    executing:  { label: 'Executing',  bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B' },
    review:     { label: 'Review',     bg: '#FED7AA', fg: '#9A3412', dot: '#EA580C' },
  };
  return (
    <div data-testid="ready-for-review" style={{
      background: '#FFFFFF',
      border: '1px solid #F59E0B40',
      borderLeft: '3px solid #F59E0B',
      borderRadius: 16, padding: 18,
      marginBottom: 18,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <AlertOctagon size={14} color="#F59E0B" />
        <span style={{
          fontSize: 11, fontWeight: 800, letterSpacing: '0.22em',
          textTransform: 'uppercase', color: '#F59E0B',
        }}>
          Additional tasks · {items.length} on demand
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>
          {reviewCount > 0 && `${reviewCount} ready to ship`}
          {reviewCount > 0 && movingCount > 0 && ' · '}
          {movingCount > 0 && `${movingCount} in motion`}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map(t => {
          const agent = normalizeOwner(t.owner_agent_id);
          const meta = AGENT_META[agent];
          const state = (t.current_state || 'backlog').toLowerCase();
          const pill = STATE_PILL[state] || STATE_PILL.backlog;
          const isReview = state === 'review';
          const isMoving = ['briefing', 'thinking', 'consulting', 'executing'].includes(state);
          return (
            <div
              key={t.task_id}
              data-testid={`additional-task-${t.task_id}`}
              onClick={() => {
                if (isReview && onReview) {
                  onReview(t);                      // Validation Modal — sacred ship moment
                } else if (onOpenInWorkshop) {
                  onOpenInWorkshop(ownerToFolder(t.owner_agent_id), t.task_id);
                }
              }}
              style={{
                padding: '12px 14px',
                background: '#FBFAF7',
                border: `1px solid ${meta.soft}40`,
                borderRadius: 12,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 12,
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = `0 4px 14px ${meta.soft}30`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: meta.gradient,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                position: 'relative',
              }}>
                <meta.Icon size={14} color="#FFFFFF" />
                {isMoving && (
                  <span style={{
                    position: 'absolute', inset: -3,
                    borderRadius: '50%', border: `1.5px solid ${meta.accent}`,
                    opacity: 0.5,
                    animation: 'pulse-glow 1.6s ease-in-out infinite',
                  }} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3,
                }}>
                  <span style={{
                    fontSize: 10, fontWeight: 800, color: meta.accent,
                    letterSpacing: '0.14em', textTransform: 'uppercase',
                  }}>
                    {meta.name}
                  </span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    fontSize: 9.5, fontWeight: 700, color: pill.fg,
                    background: pill.bg, padding: '2px 8px', borderRadius: 999,
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                  }}>
                    <span style={{
                      width: 5, height: 5, borderRadius: '50%',
                      background: pill.dot,
                      boxShadow: isMoving ? `0 0 5px ${pill.dot}` : 'none',
                    }} />
                    {pill.label}
                  </span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0F0F1A' }}>{t.title}</div>
              </div>
              {isReview ? (
                <span style={{
                  fontSize: 10, fontWeight: 700, color: '#FFFFFF',
                  background: '#F59E0B', padding: '6px 12px', borderRadius: 999,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                }}>
                  Preview &amp; Ship
                </span>
              ) : (
                <span style={{
                  fontSize: 9.5, color: meta.accent, fontStyle: 'normal',
                  letterSpacing: '0.06em', fontWeight: 700,
                  textTransform: 'uppercase',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                  {state === 'backlog' ? 'Open workshop →' : 'Working — open →'}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ====================== TODAY'S TRIANGLE ====================== */
function TodaysTriangle({ atoms, questions, onShip, onRun, onSelect, composing }) {
  const grouped = useMemo(() => {
    const out = { aria: [], lyra: [], hermes: [] };
    for (const a of atoms || []) {
      const k = normalizeOwner(a.owner_agent_id);
      if (out[k]) out[k].push(a);
    }
    return out;
  }, [atoms]);

  // Build a map { task_id → question that blocks it } so we can render the lock state.
  const blockedBy = useMemo(() => {
    const map = {};
    for (const q of questions || []) {
      if (q.status !== 'open') continue;
      for (const tid of q.blocks_task_ids || []) {
        if (!map[tid]) map[tid] = q;
      }
    }
    return map;
  }, [questions]);

  return (
    <div data-testid="todays-triangle">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Rocket size={14} color="#7C3AED" />
        <span style={{
          fontSize: 11, fontWeight: 800, letterSpacing: '0.22em',
          textTransform: 'uppercase', color: '#0F0F1A',
        }}>
          Open Atoms · {atoms.length} to ship
        </span>
        <span style={{ flex: 1 }} />
        {composing && <ComposingChip />}
        {!composing && (
          <span style={{ fontSize: 11, color: '#9CA3AF' }}>
            atoms vanish here once shipped + validated
          </span>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {['aria', 'lyra', 'hermes'].map(key => {
          const meta = AGENT_META[key];
          const items = grouped[key] || [];
          return (
            <div key={key} data-testid={`triangle-${key}`} style={{
              background: '#FFFFFF',
              border: `1px solid ${meta.soft}40`,
              borderTop: `3px solid ${meta.accent}`,
              borderRadius: 14, padding: 14,
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: meta.gradient,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <meta.Icon size={13} color="#FFFFFF" />
                </div>
                <div>
                  <div style={{
                    fontSize: 11, fontWeight: 800, letterSpacing: '0.16em',
                    color: meta.accent, textTransform: 'uppercase',
                  }}>{meta.name} · {meta.pillar}</div>
                  <div style={{ fontSize: 10, color: '#9CA3AF' }}>{items.length} atom{items.length !== 1 ? 's' : ''}</div>
                </div>
              </div>
              {items.length === 0 && (
                composing ? <SkeletonRow count={3} height={56} /> : (
                  <div style={{
                    padding: 14, textAlign: 'center', fontSize: 11,
                    color: '#9CA3AF', fontStyle: 'italic',
                  }}>
                    No atoms for this pillar today
                  </div>
                )
              )}
              {items.map(t => (
                <AtomCard
                  key={t.task_id}
                  task={t}
                  blocker={blockedBy[t.task_id]}
                  meta={meta}
                  onShip={() => onShip(t.task_id)}
                  onRun={() => onRun?.(t.task_id)}
                  onSelect={onSelect ? () => onSelect(t) : undefined}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AtomCard({ task, blocker, meta, onShip, onRun, onSelect }) {
  const priColor = task.priority === 'P0' ? '#EF4444' : task.priority === 'P1' ? '#F59E0B' : '#9CA3AF';
  const shippedStates = ['briefing', 'thinking', 'consulting', 'executing', 'review', 'done'];
  const shipped = shippedStates.includes(task.current_state);
  const locked = !!blocker;
  // Hermes lead-finding is the first wired autonomous recipe.
  const canRun =
    task.current_state === 'briefing'
    && normalizeOwner(task.owner_agent_id) === 'hermes'
    && task.validation_action === 'add_to_pipeline';
  return (
    <div
      data-testid={`atom-${task.task_id}`}
      onClick={onSelect}
      style={{
        padding: '10px 12px',
        background: locked ? '#FAFAFA' : '#FBFAF7',
        border: `1px solid ${meta.soft}30`,
        borderRadius: 10,
        opacity: locked ? 0.7 : 1,
        cursor: onSelect ? 'pointer' : 'default',
        transition: 'transform 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={onSelect ? (e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(15,15,30,0.06)';
      } : undefined}
      onMouseLeave={onSelect ? (e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.12em',
          color: priColor, padding: '1px 5px', borderRadius: 4,
          background: `${priColor}15`,
        }}>{task.priority || 'P1'}</span>
        {task.irreversible && (
          <span title="Irreversible · requires founder validation"
            style={{ fontSize: 11, color: '#EF4444' }}>⚠</span>
        )}
        <span style={{ flex: 1 }} />
        {task.distance_to_dollar === 1 && (
          <span title="Direct path to revenue"
            style={{ fontSize: 9, fontWeight: 700, color: '#10B981',
                     background: '#10B98115', padding: '1px 6px', borderRadius: 4 }}>$1</span>
        )}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#0F0F1A', lineHeight: 1.35, marginBottom: 6 }}>
        {task.title}
      </div>
      {locked && (
        <div style={{ fontSize: 10, color: '#EF4444', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
          <AlertOctagon size={10} /> blocked by {AGENT_META[normalizeOwner(blocker.asked_by)].name}&apos;s question
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 10, color: '#9CA3AF', flex: 1 }}>
          {task.eta_minutes || 30}min · {(task.current_state || 'backlog').toUpperCase()}
        </span>
        {!locked && !shipped && (
          <button
            data-testid={`ship-atom-${task.task_id}`}
            onClick={(e) => { e.stopPropagation(); onShip(); }}
            style={{
              fontSize: 10, fontWeight: 700, color: '#FFFFFF',
              background: meta.accent, padding: '5px 11px', borderRadius: 999,
              letterSpacing: '0.12em', textTransform: 'uppercase',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
              transition: 'transform 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateX(2px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateX(0)'}
          >
            <Send size={10} /> Ship
          </button>
        )}
        {shipped && (
          <>
            {canRun && (
              <button
                data-testid={`run-atom-${task.task_id}`}
                onClick={(e) => { e.stopPropagation(); onRun(); }}
                style={{
                  fontSize: 10, fontWeight: 700, color: '#FFFFFF',
                  background: meta.gradient, padding: '5px 11px', borderRadius: 999,
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 4,
                  transition: 'transform 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateX(2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateX(0)'}
              >
                ▶ Run
              </button>
            )}
            {!canRun && (
              <span style={{
                fontSize: 10, fontWeight: 700, color: meta.accent,
                background: `${meta.soft}30`, padding: '4px 10px', borderRadius: 999,
                letterSpacing: '0.12em', textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <CheckCircle2 size={10} /> shipped
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
